import { page } from '$app/state';
import { BookingStatus, type Booking, type BookingApi } from '$lib/model/booking';
import { apiStore } from '$lib/stores/api';
import { bookingIsConfirmed, convertBookingApiToBooking } from '$lib/stores/booking';
import { businessStore } from '$lib/stores/business';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { bookingGetPaymentSummary } from '../../routes/(app)/(connected)/(app)/dashboard/calendar/_components/payment';

export type Result = {
	color: string;
	autoHide: boolean;
	sound?: string;
	vibrate?: () => void;
	title?: string;
	description?: string;
};

export type Scan = {
	time: DateTime;
	code: string;
	resultKey: string;
	apiSync: boolean;
	bookingId?: string;
	pax?: number;
};

export type ApiScan = {
	id: number;
	time: string;
	code: string;
	bookingId: string;
	pax: number;
};

export const scansHistory = writable<Scan[]>([]);
export let qrCodeBookings = writable<Booking[]>([]);
export let defaultValidSound = writable('valid');
export let fullscreen = writable(false);

export function loadQrCodeData(onlyBookings: boolean = false): Promise<void> {
	return get(apiStore)!
		.get('/public/qr-code/' + page.params.token + '/' + page.params.date)
		.then((res) => {
			if (!onlyBookings) businessStore.refreshFromData(res.data.business);
			qrCodeBookings.set(
				res.data.bookings.map((b: BookingApi) => convertBookingApiToBooking(b))
			);
			if (!onlyBookings) loadScansHistory(res.data.scans);
		});
}
export function convertApiScanToScan(scan: ApiScan): Scan {
	return {
		...scan,
		time: DateTime.fromISO(scan.time),
		resultKey: 'SUCCESS',
		apiSync: true
	};
}
export function loadScansHistoryFromApi(scans: ApiScan[]) {
	scansHistory.set(scans.map((scan) => convertApiScanToScan(scan)));
}

function loadScansHistory(scans: ApiScan[]) {
	loadScansHistoryFromApi(scans);

	const scansJson = localStorage.getItem('qr-code.scans');
	if (scansJson) {
		let scansLocalStorage = JSON.parse(scansJson, (key, value) => {
			if (key === 'time') {
				return DateTime.fromISO(value);
			}
			return value;
		});
		// Remove 24h old scans
		scansLocalStorage = scansLocalStorage.filter(
			(scan: Scan) => scan.time > DateTime.now().minus({ hours: 24 })
		);

		// Merge local storage scans with API scans, excluding duplicates
		const mergedScans = scansLocalStorage.filter(
			(scanLocalStorage: Scan) =>
				!scans.some(
					(scanApi: ApiScan) =>
						scanLocalStorage.code === scanApi.code &&
						scanLocalStorage.resultKey === 'SUCCESS'
				)
		);
		scansHistory.set(
			[...get(scansHistory), ...mergedScans].sort(
				(a, b) => b.time.toMillis() - a.time.toMillis()
			)
		);
	}

	localStorage.setItem('qr-code.scans', JSON.stringify(get(scansHistory)));
}

export function addScan(
	time: DateTime,
	code: string,
	apiSync: boolean,
	resultKey: string = 'SUCCESS'
) {
	scansHistory.set([
		{
			time: time,
			code: code,
			resultKey: resultKey,
			apiSync: resultKey !== 'SUCCESS'
		},
		...get(scansHistory)
	]);
	localStorage.setItem('qr-code.scans', JSON.stringify(get(scansHistory)));
}

export function scan(test: string): keyof typeof results {
	// uuid+pax
	const bookingId = test.split('+')[0];
	const bookingPax = test.split('+')[1];

	const bookingResult = get(qrCodeBookings).find((b) => b.id === bookingId);
	if (bookingResult) {
		if (!bookingIsConfirmed(bookingResult)) {
			addScan(DateTime.now(), test, true, 'CANCELED');
			return 'CANCELED';
		}

		if (+bookingPax <= 0 || +bookingPax > bookingResult.pax) {
			addScan(DateTime.now(), test, true, 'NOT_FOUND');
			return 'NOT_FOUND';
		}

		const bookingPayment = bookingGetPaymentSummary(bookingResult);
		if (bookingPayment.remaining > 0 || bookingResult.status === BookingStatus.ToBeConfirmed) {
			addScan(DateTime.now(), test, true, 'REQUIRE_ACTION');
			return 'REQUIRE_ACTION';
		}

		if (get(scansHistory).some((scan) => scan.code === test && scan.resultKey === 'SUCCESS')) {
			addScan(DateTime.now(), test, true, 'ALREADY_SCANNED');
			return 'ALREADY_SCANNED';
		}

		addScan(DateTime.now(), test, false, 'SUCCESS');
		return 'SUCCESS';
	} else {
		addScan(DateTime.now(), test, true, 'NOT_FOUND');
		return 'NOT_FOUND';
	}
}

export const results: Record<string, Result> = {
	SUCCESS: {
		color: 'bg-success text-success-on opacity-90',
		autoHide: true,
		vibrate: () => {
			vibrate(100);
			setTimeout(() => vibrate(100), 200);
		}
	},
	ALREADY_SCANNED: {
		color: 'bg-warning text-warning-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.already-scanned.title',
		description: 'qr-code.scan.already-scanned.description'
	},
	NOT_FOUND: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.not-found.title',
		description: 'qr-code.scan.not-found.description'
	},
	CANCELED: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'down',
		vibrate: vibrateError,
		title: 'qr-code.scan.canceled.title',
		description: 'qr-code.scan.canceled.description'
	},
	REQUIRE_ACTION: {
		color: 'bg-error text-error-on',
		autoHide: false,
		sound: 'cashmachine',
		vibrate: vibrateError,
		title: 'qr-code.scan.require-action.title',
		description: 'qr-code.scan.require-action.description'
	}
};

function vibrateError() {
	vibrate(100);
	setTimeout(() => vibrate(100), 300);
	setTimeout(() => vibrate(400), 500);
}

function vibrate(duration: number): void {
	if ('vibrate' in navigator) {
		navigator.vibrate(duration); // 1000 ms = 1 second
	}
}
